// components/FormScreen.js
import React, {useEffect, useRef, useState} from 'react';
import Header from '../components/shared/Header';
import InputField from '../components/shared/InputField';
import DropdownField from '../components/shared/DropdownField';
import CheckboxField from '../components/shared/CheckboxField';
import { postCreateCustomer } from '../services/postCreateCustomer';
import Footer from '../components/shared/Footer';
import { useApp } from '../context/appContext'
import toast from 'react-hot-toast';
import '../styles/global.css';
import { fbApiUrl } from "./shared/facebooktrack_config";  
import { patchConfirmAppointment } from "../services/patchConfirmAppointment"
import errorIcon from '../assets/icons/error.svg';
import { getAssetUrl, haversineDistance } from '../utils/lib';


const steps = [
  { name: 'Select the location', link: 'listing', isActive: true },
  { name: 'Appointment Details', link: 'date-picker', isActive: true },
  { name: 'Contact Details', link: 'form', isActive: true }
];


const FormScreen = () => {


  useEffect(() => {
    const sendDataToFacebookAPI_Form = async () => {
        if (!fbApiUrl) {
          console.error("Error: fbApiUrl is not defined!");
        } 
        else {
          try{
        
            const ipResponse = await fetch("https://api64.ipify.org?format=json");
            const ipData = await ipResponse.json();
            const clientIp = ipData.ip; // Extracted IP Address

            // Get User Agent
            const userAgent = navigator.userAgent;

            const data = {
              data: [
                {
                  event_name: "View Booking Form",
                  event_time: Math.floor(Date.now() / 1000),
                  user_data: {
                    client_ip_address: clientIp,
                    client_user_agent: userAgent,
                  },
                  action_source: "website",
                },
              ],
              test_event_code: "TEST58912",
            };
          
            const response = await fetch(fbApiUrl, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(data),
            });
        
            const result = await response.json();
          }
          catch (error) {
            console.error("Error sending data to Facebook API:", error);
          }
        }
    };

    sendDataToFacebookAPI_Form();
  }, []); // Runs only on component mount


  const [formErrors, setFormErrors] = useState({}) // State to track validation errors
  const formRef = useRef(null);
  const { form, setForm, setCurrentPage } = useApp();
  const [loading, setLoading] = useState(false);
  const [submissionFailed, setSubmissionFailed] = useState(false);

  const handleInputChange = (key, value) => {
    setForm((prevForm) => ({
      ...prevForm,
      bookingForm: {
        ...prevForm.bookingForm,
        [key]: value, // Update the specific field in bookingForm
      }
    }));
  };

  // Validation function
  const validateForm = () => {
    const errors = {}

    if (!form.bookingForm.salutation) {
      errors.salutation = 'Salutation is required';
    }
    if (!form.bookingForm.firstName) {
      errors.firstName = 'First name is required';
    }
    if (!form.bookingForm.lastName) {
      errors.lastName = 'Last name is required';
    }
    if (!form.bookingForm.personEmail) {
      errors.personEmail = 'Email address is required';
    } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(form.bookingForm.personEmail)) {
      errors.personEmail = 'Invalid email address';
    }
    if (!form.bookingForm.personMobilePhone) {
      errors.personMobilePhone = 'Mobile phone number is required';
    } else if (!/^\d{10}$/.test(form.bookingForm.personMobilePhone)) {
      errors.personMobilePhone = 'Please enter a valid phone number';
    }

    return errors
  }


  const backToSelectDateTime = async (e) => {
    e.preventDefault();
    setCurrentPage("date-picker");
  }

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); // Start loading
    setSubmissionFailed(false); // Reset on new attempt


    // Submit logic goes here
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors); // Set errors if validation fails
      toast.error("Please fill in all required fields");
      setLoading(false); // Stop loading if validation fails
    } else {
      setFormErrors({}); // Clear errors if form is valid

      try {
        const res = await postCreateCustomer(form.bookingForm) 
         

        if(res?.id) {

          setForm((prevForm) => ({
            ...prevForm,
            clientId: res?.id
          }))

          if (form?.appointmentId && res?.id && form?.bookingForm?.personEmail) {
            setLoading(true);
            const confirmRes = await patchConfirmAppointment(form?.appointmentId, res?.id, form?.bookingForm?.personEmail);
            setLoading(false);
            if (confirmRes?.status === "Confirmed") {
              toast.success("Appointment confirmed successfully!");
              setCurrentPage("confirmation")
            } else {
              setSubmissionFailed(true);
              // toast.error("Unfortunately the time slot you requested is no longer available. Please choose another.");
            }
          }
        } else {
          // toast.error(res.response?.data || "Something went wrong.");
          setSubmissionFailed(true);
        }
      }
      catch (error) {
        // toast.error("An error occurred while submitting the form.");
        setSubmissionFailed(true);
      } finally {
        setLoading(false); // Always stop loading after request completes
      }
    }

    
  };


  const handleSelectNewDateTime = (e) => {
    e.preventDefault();
    setCurrentPage("date-picker");
  };



  useEffect(() => {
    if(!form.time) setCurrentPage("map")
  }, [form.time, setCurrentPage])


  useEffect(() => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  useEffect(() => {
      // Store the disabled Bootstrap stylesheet(s)
      const disabledStylesheets = [];

      // Step 1: Loop through all stylesheets and disable only Bootstrap CSS
      document.querySelectorAll('link[rel="stylesheet"]').forEach((stylesheet) => {
          const href = stylesheet.getAttribute('href');
          if (href && href.includes('bootstrap')) {
              stylesheet.disabled = true; // Disable the Bootstrap stylesheet
              disabledStylesheets.push(stylesheet); // Store for later re-enabling
              console.log(`Disabled Bootstrap CSS: ${href}`);
          }
      });

      // Step 2: Cleanup: Re-enable disabled stylesheets on unmount
      return () => {
          disabledStylesheets.forEach((stylesheet) => {
              stylesheet.disabled = false; // Re-enable the Bootstrap stylesheet
              console.log(`Re-enabled Bootstrap CSS: ${stylesheet.getAttribute('href')}`);
          });
      };
  }, []);

  return (
    <div className="main-container">
      <Header
          formRef={formRef}
          title="Book an Eye Test"
          steps={steps}
          subheading="Complete Booking"
          pageinfo={
    <span
      dangerouslySetInnerHTML={{
        __html: "To finalise your online booking, please enter details of the person having this eye test. <br/> Optical Superstore collects your personal information in accordance with our <a href='https://opticalsuperstore.com.au/privacy-policy/' target='_blank' rel='noreferrer noopener'>Privacy Policy.</a>",
      }}
    />
  }
      />

    {form?.time && (
      <>
        <input type="hidden" name="appointmentDate" value={form.time.appointmentDate} />
        <input type="hidden" name="startTimeReadable" value={form.time.startTimeReadable} />
      </>
    )}



      <div className="sub-container my-form-sub-container">
        <form  className="form-container" onSubmit={handleFormSubmit}>

          {/*   <DropdownField 
            label="Salutation"
            options={["Mr.", "Mrs.", "Miss.", "Ms.", "Mx."]}
            value={form.bookingForm.salutation}
            onChange={(e) => handleInputChange('salutation', e.target.value)}
            error={formErrors.salutation} // Pass error to DropdownField
          />*/}

          <InputField 
            label="First Name"
            type="text"
            required={true}
            value={form.bookingForm.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            error={formErrors.firstName} // Pass error to InputField
          />

          <InputField 
            label="Last Name" 
            type="text" 
            required={true} 
            value={form.bookingForm.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)} 
            error={formErrors.lastName} // Pass error to InputField
          />

          <InputField 
            label="Email Address" 
            type="email" 
            required={true} 
            value={form.bookingForm.personEmail}
            onChange={(e) => handleInputChange('personEmail', e.target.value)}
            error={formErrors.personEmail} // Pass error to InputField
          />

          <InputField 
            label="Mobile" 
            type="tel" 
            required={true}
            value={form.bookingForm.personMobilePhone}
            onChange={(e) => handleInputChange('personMobilePhone', e.target.value)}
            error={formErrors.personMobilePhone} // Pass error to InputField
          />

          <InputField 
            label="Other Phone" 
            type="tel" 
            value={form.bookingForm.personHomePhone}
            onChange={(e) => handleInputChange('personHomePhone', e.target.value)}
            error={formErrors.personHomePhone} // Pass error to InputField
          />

          <DropdownField
            label="Are you Diabetic?"
            options={["No", "Yes"]}
            value={form.bookingForm.Diabetes__pc}
            onChange={(e) => handleInputChange('Diabetes__pc', e.target.value)}
          />

          {/* <CheckboxField 
            label="New Customer" 
            checked={form.bookingForm.First_Appointment__c}
            onChange={(e) => handleInputChange('First_Appointment__c', e.target.checked)}
          /> */}

          <CheckboxField 
            label="I would like to opt into marketing Material" 
            checked={form.bookingForm.Marketing_Opt_In__c}
            onChange={(e) => handleInputChange('Marketing_Opt_In__c', e.target.checked)}
            required={false}
          />

          {/* <CheckboxField 
            label="Don't send booking confirmation" 
            checked={form.bookingForm.Dont_Confirm__c}
            onChange={(e) => handleInputChange('Dont_Confirm__c', e.target.checked)}
          /> */}

          {/* <CheckboxField 
            label="Communication Preferences" 
            checked={form.bookingForm.Communication_Preferences__c}
            onChange={(e) => handleInputChange('Communication_Preferences__c', e.target.checked)}
          /> */}
          <Footer />
          <button className="submit-button my-submit-button form-submit" disabled={loading} onClick={handleFormSubmit} type="submit">{loading ? (<span className="loader_form_screen"></span>) : ("Complete Booking")}</button>
          {submissionFailed && (
            <div className='error-button-div'>
              <img className="error-icon" src={getAssetUrl(errorIcon)} alt="Error Icon" /><p>This appointment time is no longer available. Please&nbsp;<a href="#" onClick={handleSelectNewDateTime}>select a new date/time.</a></p>
            </div>
            )}
            
        </form>
      </div>
      
    </div>
  );
};

export default FormScreen;
